// How an outward-facing write's failure is interpreted — and why the obvious reading is wrong.
//
// Three endpoints in this client reach OUTSIDE Woo: `addToAtsJobAsync` writes into a
// customer's ATS, `exportCandidates` pushes profiles to one, and the message send puts mail in
// a recruiter's outbox. All three are enqueued server-side and deduped, and that changes what
// a failure means.
//
// ⚠️ A NETWORK FAILURE MUST BE REPORTED AS SUCCESS, AND THAT IS NOT A SHORTCUT. Upstream's
// comment is explicit (`extension-alert-candidates-add-to-job.component.ng1.ts:128`):
// `status <= 0` — network, timeout, abort — means the request may well have reached the server,
// and the write is deduped and idempotent, so it will still land. Reporting failure there tells
// the recruiter nothing happened when it did, and the natural response is to press Add again.
// Only a server that ANSWERED can say the write did not happen.
import { ApiError } from './request.ts'

export type OutwardWriteOutcome =
  /** Enqueued, or may yet be — reflect it in the UI. */
  | { report: 'success' }
  /** The server refused and the auth layer already owns it. Say nothing. */
  | { report: 'silent' }
  /** The server refused before enqueue. Tell the user, with its words where it gave any. */
  | { report: 'error'; message: string }

export const OUTWARD_WRITE_FALLBACK_MESSAGE = 'Something went wrong adding to the job, please try again.'

/**
 * Classify a rejection from one of the three writes.
 *
 * ⚠️ `ApiError` IS THE "SERVER ANSWERED" SIGNAL, and it is the whole distinction. `request()`
 * throws `ApiError` only after a response (`!response.ok`); a `fetch` that never completes
 * rejects with a `TypeError` instead. That maps exactly onto upstream's `status > 0` test
 * without this code having to invent a status of its own.
 *
 * ⚠️ AND 403 IS SILENT ON PURPOSE. It is a session or permission failure that the auth layer
 * handles — upstream's http interceptor swallows it, and raising a dialog here would put a
 * second, worse explanation in front of a user who is about to be redirected anyway.
 */
export function outwardWriteOutcome(error: unknown, fallbackMessage = OUTWARD_WRITE_FALLBACK_MESSAGE): OutwardWriteOutcome {
  if (!(error instanceof ApiError)) {
    // No response: deduped and idempotent, so it will still land.
    return { report: 'success' }
  }
  if (error.status === 403) return { report: 'silent' }
  return { report: 'error', message: serverMessage(error) ?? fallbackMessage }
}

/**
 * `resp.data.error.message` — the server's own words, where it gave any.
 *
 * Narrowed step by step rather than with a cast: the body is whatever the endpoint returned,
 * and an `as` here would turn a shape change into a crash inside an error handler, which is
 * the worst place to learn about one.
 */
export function serverMessage(error: ApiError): string | undefined {
  const body: unknown = error.body
  if (typeof body !== 'object' || body === null) return undefined
  const inner: unknown = (body as { error?: unknown }).error
  if (typeof inner !== 'object' || inner === null) return undefined
  const message: unknown = (inner as { message?: unknown }).message
  return typeof message === 'string' && message.length > 0 ? message : undefined
}
